// components/InvoiceRequest/InvoiceRequestDialog.tsx
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormHelperText,
  Alert,
  CircularProgress,
  Box,
  FormControlLabel,
  Switch,
  InputAdornment
} from '@mui/material';
import { invoiceRequestService } from '../../services/invoiceRequest';
import { useUser } from '../../hooks/useUser';
import { NONAME } from 'dns';
import { NumberTextField } from '../Common/NumberTextField';

interface InvoiceRequestDialogProps {
  open: boolean;
  onClose: () => void;
  constructionId: number;
  onSuccess: () => void;
}

export const InvoiceRequestDialog: React.FC<InvoiceRequestDialogProps> = ({
  open,
  onClose,
  constructionId,
  onSuccess
}) => {
  const { userInfo } = useUser();
  const [billingMonth, setBillingMonth] = useState<string>(
    new Date().toISOString().substring(0, 7) // YYYY-MM 形式
  );
  const [remarks, setRemarks] = useState('');
  const [estimatedAmount, setEstimatedAmount] = useState<number | null>(null); // 見積金額
  const [invoiceAmount, setInvoiceAmount] = useState<string>(''); // 請求金額
  const [useEstimatedAmount, setUseEstimatedAmount] = useState(true); // 見積金額を使用するフラグ
  const [loading, setLoading] = useState(false);
  const [loadingEstimate, setLoadingEstimate] = useState(false);
  const [error, setError] = useState('');
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  // 初期表示時に見積金額を取得
  useEffect(() => {
    if (open && constructionId) {
      fetchEstimatedAmount();
    }
  }, [open, constructionId]);

  // 見積金額を取得
  const fetchEstimatedAmount = async () => {
    setLoadingEstimate(true);
    try {
      const amount = await invoiceRequestService.getConstructionEstimatedAmount(constructionId);
      setEstimatedAmount(amount);
      setInvoiceAmount(amount.toString());
    } catch (err) {
      console.error('見積金額の取得に失敗しました:', err);
      setEstimatedAmount(0);
      setInvoiceAmount('0');
    } finally {
      setLoadingEstimate(false);
    }
  };

  // 請求月変更時に重複チェック状態をリセット
  const handleBillingMonthChange = (value: string) => {
    setBillingMonth(value);
    setIsDuplicate(false);
    setError('');
  };

  // 入力フィールドは数値のみ許可
  const handleInvoiceAmountChange = (value: string) => {
    // 数値のみを許可（カンマなし）
    const numericValue = value.replace(/[^0-9]/g, '');
    setInvoiceAmount(numericValue);
  };

  // 見積金額を使用する設定の切り替え
  const handleUseEstimatedAmount = (checked: boolean) => {
    setUseEstimatedAmount(checked);
    if (checked && estimatedAmount !== null) {
      setInvoiceAmount(estimatedAmount.toString());
    }
  };

  // 重複チェック
  const checkDuplicate = async (): Promise<boolean> => {
    if (!billingMonth) return false;

    setIsChecking(true);
    try {
      const formattedDate = `${billingMonth}-01`;
      const isDuplicate = await invoiceRequestService.checkDuplicate(
        constructionId
        // formattedDate
      );

      setIsDuplicate(isDuplicate);
      if (isDuplicate) {
        setError('この工事は既に依頼が登録されています');
      }

      return isDuplicate;
    } catch (err) {
      console.error('重複チェックに失敗しました:', err);
      setError('重複チェックに失敗しました');
      return false;
    } finally {
      setIsChecking(false);
    }
  };

  const handleSubmit = async () => {
    if (!billingMonth) {
      setError('請求月を選択してください');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // まず重複チェックを実行
      const isDuplicate = await checkDuplicate();
      if (isDuplicate) {
        setLoading(false);
        return; // 重複があれば処理中止
      }

      // YYYY-MM-01 形式に変換
      const formattedDate = `${billingMonth}-01`;
      const createdByName = userInfo?.lastName || "NONAME";

      // 請求金額の処理
      // ここが問題かもしれません。invoiceAmountを明示的に渡す必要があります
      const finalInvoiceAmount = useEstimatedAmount
        ? (estimatedAmount ?? 0) // null の場合は 0 を使用
        : (invoiceAmount ? parseInt(invoiceAmount) : 0);


      console.log('送信する請求金額:', finalInvoiceAmount); // デバッグ用

      await invoiceRequestService.create({
        constructionId,
        billingMonth: formattedDate,
        remarks,
        createdByName,
        invoiceAmount: finalInvoiceAmount // 必ず値を設定する
      });

      onSuccess();
      handleClose();
    } catch (err: any) {
      console.error('請求書作成依頼の登録に失敗しました:', err);
      // APIからの409 Conflictエラーを処理
      if (err.response && err.response.status === 409) {
        setIsDuplicate(true);
        setError('この工事は既に依頼が登録されています');
      } else {
        setError('請求書作成依頼の登録に失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };


  const handleClose = () => {
    setBillingMonth(new Date().toISOString().substring(0, 7));
    setRemarks('');
    setInvoiceAmount('');
    setUseEstimatedAmount(true);
    setError('');
    setIsDuplicate(false);
    onClose();
  };


  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableRestoreFocus>
      <DialogTitle sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'grey.50',
        px: 3,
        py: 2
      }}>
        請求書作成依頼
      </DialogTitle>
      <DialogContent sx={{ p: 3, pt: 3 }}>
        <Box sx={{ mb: 3 }}>
          <TextField
            label="請求月"
            type="month"
            value={billingMonth}
            onChange={(e) => handleBillingMonthChange(e.target.value)}
            fullWidth
            variant="outlined"
            error={!!error && !billingMonth}
            helperText={!!error && !billingMonth ? '請求月を選択してください' : ''}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: isChecking ? <CircularProgress size={20} /> : null
            }}
            sx={{
              '& input': {
                py: { xs: 1.5, md: 2 }
              }
            }}
          />
        </Box>

        {/* 見積金額（表示のみ） */}
        <Box sx={{ mb: 3 }}>
          <TextField
            label="見積金額"
            value={loadingEstimate ? "読み込み中..." : (estimatedAmount !== null ? `¥${estimatedAmount.toLocaleString()}` : '-')}
            fullWidth
            variant="outlined"
            InputProps={{
              readOnly: true,
              startAdornment: loadingEstimate ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null
            }}
          />
        </Box>

        {/* 請求金額 */}
        <Box sx={{ mb: 3 }}>
          <NumberTextField
            label="請求金額"
            value={invoiceAmount}
            onChange={(e) => handleInvoiceAmountChange(e.target.value)}
            fullWidth
            variant="outlined"
            disabled={useEstimatedAmount || loadingEstimate}
            thousandSeparator={true}
            InputProps={{
              startAdornment: <InputAdornment position="start">¥</InputAdornment>
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={useEstimatedAmount}
                onChange={(e) => handleUseEstimatedAmount(e.target.checked)}
                disabled={loadingEstimate}
              />
            }
            label="見積金額と同じ"
            sx={{ mt: 1 }}
          />
        </Box>

        <TextField
          label="申し送り事項"
          fullWidth
          multiline
          rows={4}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          variant="outlined"
        />

        {isDuplicate && (
          <Alert severity="error" sx={{ mt: 2 }}>
            この工事は既に依頼が登録されています
          </Alert>
        )}

        {error && !isDuplicate && <FormHelperText error sx={{ mt: 1 }}>{error}</FormHelperText>}
      </DialogContent>
      <DialogActions sx={{
        borderTop: 1,
        borderColor: 'divider',
        px: 3,
        py: 2
      }}>
        <Button
          onClick={handleClose}
          color="primary"
          disabled={loading}
          variant="outlined"
        >
          キャンセル
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading || isDuplicate || isChecking || loadingEstimate}
        >
          {loading ? '送信中...' : '依頼する'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};